import { useContext, useState } from "react"
import { Modal, ModalBody, ModalHeader } from "flowbite-react"
import { VibratoClientContext } from "./contexts"
import { Search, Building, NavArrowLeft, NavArrowRight } from "iconoir-react"
import { formatAnyPhoneNumber } from "@/lib/phone"

type SimpleBusiness = {
  uuid: string
  name: string
  address: string | null
  phone: string | null
  country_code: string | null
  city: string | null
  state: string | null
}

export default function BusinessSearchModal({
  show,
  onClose,
  onSelect
}: {
  show: boolean
  onClose: () => void
  onSelect: (biz: SimpleBusiness) => void
}) {
  const vibratoClient = useContext(VibratoClientContext)
  const [query, setQuery] = useState("")
  const [location, setLocation] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [results, setResults] = useState<SimpleBusiness[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [totalResults, setTotalResults] = useState(0)
  const [hasSearched, setHasSearched] = useState(false)

  const pageSize = 10

  const performSearch = async (page: number = 1) => {
    if (!vibratoClient || !query.trim()) return

    try {
      setIsLoading(true)
      const resp = await vibratoClient.searchBusinessesSimple({
        query,
        location: location || undefined,
        page,
        page_size: pageSize
      })
      setResults(resp.results as any)
      setTotalResults(resp.total)
      setCurrentPage(page)
      setHasSearched(true)
    } catch (e) {
      console.error("Business search failed", e)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSearch = () => {
    performSearch(1)
  }

  const handleNextPage = () => {
    if (currentPage * pageSize < totalResults) {
      performSearch(currentPage + 1)
    }
  }

  const handlePrevPage = () => {
    if (currentPage > 1) {
      performSearch(currentPage - 1)
    }
  }

  const totalPages = Math.ceil(totalResults / pageSize)
  const hasNextPage = currentPage < totalPages
  const hasPrevPage = currentPage > 1

  return (
    <Modal show={show} onClose={onClose} className="pt-safe">
      <ModalHeader className="border-0">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Search className="w-5 h-5 text-purple-600" />
          </div>
          <span className="text-xl font-semibold text-gray-900">Find Your Business</span>
        </div>
      </ModalHeader>
      <ModalBody>
        <div className="flex flex-col gap-6">
          {/* Search Inputs */}
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Business Name</label>
              <div className="relative">
                <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                  placeholder="Enter the business name"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Location (Optional)</label>
              <input
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                placeholder="City, State (e.g., Austin, TX)"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              />
            </div>
            <div className="flex justify-center">
              <button
                onClick={handleSearch}
                disabled={!query.trim() || isLoading}
                className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-medium shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center gap-2"
              >
                <Search className="w-5 h-5" />
                {isLoading ? "Searching..." : "Search Businesses"}
              </button>
            </div>
          </div>

          {/* Search Results */}
          {hasSearched && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Search Results</h3>
                {totalResults > 0 && (
                  <span className="text-sm text-gray-500">
                    {totalResults} result{totalResults !== 1 ? "s" : ""} found
                  </span>
                )}
              </div>

              {isLoading ? (
                <div className="flex flex-col items-center justify-center h-40 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mb-3" />
                  <p className="text-gray-600 text-sm">Searching businesses...</p>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    {results.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-32 bg-gray-50 rounded-xl border border-gray-200">
                        <Search className="w-8 h-8 text-gray-400 mb-2" />
                        <p className="text-gray-500 text-sm">No businesses found</p>
                        <p className="text-xs text-gray-400 mt-1">Try a different search term or location</p>
                      </div>
                    ) : (
                      results.map((biz) => (
                        <button
                          key={biz.uuid}
                          onClick={() => {
                            onSelect(biz)
                            onClose()
                          }}
                          className="w-full text-left p-4 bg-white border border-gray-200 rounded-xl hover:border-purple-300 hover:bg-purple-50 transition-all duration-200 group"
                        >
                          <div className="flex items-start gap-3">
                            <div className="p-2 bg-gray-100 group-hover:bg-purple-100 rounded-lg transition-colors">
                              <Building className="w-5 h-5 text-gray-600 group-hover:text-purple-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-gray-900 group-hover:text-purple-900 mb-1">
                                {biz.name}
                              </div>
                              <div className="text-sm text-gray-600 mb-1">
                                {[biz.city, biz.state].filter(Boolean).join(", ")}
                                {biz.phone && biz.country_code && (
                                  <span className="ml-2 px-2 py-1 bg-gray-100 rounded-md text-xs font-medium">
                                    {formatAnyPhoneNumber(biz.country_code, biz.phone)}
                                  </span>
                                )}
                              </div>
                              {biz.address && <div className="text-xs text-gray-500 truncate">{biz.address}</div>}
                            </div>
                          </div>
                        </button>
                      ))
                    )}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                      <button
                        onClick={handlePrevPage}
                        disabled={!hasPrevPage}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <NavArrowLeft className="w-4 h-4" />
                        Previous
                      </button>

                      <span className="text-sm text-gray-700">
                        Page {currentPage} of {totalPages}
                      </span>

                      <button
                        onClick={handleNextPage}
                        disabled={!hasNextPage}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Next
                        <NavArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </>
              )}

              <div className="text-center pt-4 border-t border-gray-100">
                <p className="text-sm text-gray-500">
                  Can't find your business? Email us at{" "}
                  <a
                    href="mailto:contact@getvibrato.com?subject=Business Not Found - Verification Request"
                    className="text-purple-600 hover:text-purple-700 font-medium underline"
                  >
                    contact@getvibrato.com
                  </a>
                </p>
              </div>
            </div>
          )}
        </div>
      </ModalBody>
    </Modal>
  )
}
